import {
  FIRST_ADULT_AGE,
  formatGroupHeading,
  groupingBasisOf,
  oldestGroupPlacedByAge,
} from 'utils/groupLabel';

// Age filter helpers shared by the Club / Guest / Visitor Athletes pages.
//
// The Age filter is a multi-select. Its stored value is an ARRAY of option
// values, where an empty array means "all ages" (no filtering). Option values
// are the group numbers the club places by age, as strings ('5' … '20' for an
// Under club, whose last is U20), plus two range groups for its adults:
//   'senior'  = 20-34 (in an Under club it overlaps U20, because a 20 year old
//               shares that number)
//   'masters' = 35+
// A school has no adults, so it has neither range group: every number is a
// choice of its own.
// Ticked values are OR'd together, so a club that runs U8 and U9 as one age
// range can tick both and see the whole range at once.

export const SENIOR_VALUE = 'senior';
export const MASTERS_VALUE = 'masters';

const isSenior = (age) => age >= FIRST_ADULT_AGE && age <= 34;
const isMasters = (age) => age >= 35;

/**
 * The choices of the Age filter for a list of athletes, from their group
 * numbers and the club. A number the club places by age is a choice of its
 * own, labelled as the club reads it ("U11", "11", "Y6"). Senior and Masters
 * are offered only to a club that has adults, and only when an athlete in the
 * list falls in them.
 */
export function ageFilterOptions(groupNumbers, club) {
  const distinct = Array.from(
    new Set(groupNumbers.map(Number).filter((v) => Number.isFinite(v)))
  ).sort((a, b) => a - b);

  const basis = groupingBasisOf(club);
  const oldest = oldestGroupPlacedByAge(club);
  const hasAdults = oldest !== Infinity;

  const options = distinct
    .filter((number) => number <= oldest)
    .map((number) => ({
      value: String(number),
      label: formatGroupHeading(basis, number),
    }));
  if (hasAdults && distinct.some(isSenior)) {
    options.push({ value: SENIOR_VALUE, label: 'Senior' });
  }
  if (hasAdults && distinct.some(isMasters)) {
    options.push({ value: MASTERS_VALUE, label: 'Masters' });
  }
  return options;
}

/**
 * Coerce a persisted filter value to the array form. Earlier builds stored a
 * single string ('all' or one option value); a session still carrying that
 * shape is upgraded here so callers never see a string.
 */
export function normaliseAgeFilter(stored) {
  if (Array.isArray(stored)) return stored;
  if (typeof stored === 'string' && stored !== '' && stored !== 'all') {
    return [stored];
  }
  return [];
}

/**
 * The selection that is actually in force: the normalised stored value with
 * anything that is no longer an offered option dropped. Keeps the trigger text
 * and the row filtering in agreement when the data changes underneath a
 * persisted selection (e.g. the last U12 athlete was removed).
 */
export function activeAgeFilter(stored, options) {
  const values = normaliseAgeFilter(stored);
  if (values.length === 0) return values;
  const known = new Set(options.map((o) => o.value));
  return values.filter((v) => known.has(v));
}

/** Whether an athlete's ageGroup satisfies the ticked values (OR). */
export function matchesAgeFilter(ageGroup, selectedValues) {
  if (selectedValues.length === 0) return true;
  const age = Number(ageGroup);
  return selectedValues.some((v) => {
    if (v === SENIOR_VALUE) return isSenior(age);
    if (v === MASTERS_VALUE) return isMasters(age);
    return String(ageGroup) === v;
  });
}
